/**
 * Pure URL helpers for the Preview iframe, extracted from Preview.tsx so the
 * reload/cache-bust logic is unit-testable outside the React effects.
 */

function defaultOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
}

/**
 * Append a cache-busting `_r` query param so the iframe reloads fresh content
 * after a build. Handles relative preview URLs (e.g. `/p/<chatId>/`) too.
 */
export function appendReloadParam(url: string, nonce: number, baseOrigin?: string): string {
  try {
    const u = new URL(url, baseOrigin ?? defaultOrigin());
    u.searchParams.set('_r', String(nonce));

    return u.toString();
  } catch (err) {
    console.warn('[Preview] appendReloadParam: could not parse URL', url, err);

    return url;
  }
}

/**
 * Decide which URL the iframe should reload after a generation: keep the
 * current src (preserving the user's path + query) unless the server handed
 * us a fresh preview URL on a DIFFERENT origin — that means the pod (and its
 * port-forward) was recreated and the current origin points at a dead port.
 */
export function pickReloadUrl(currentSrc: string, freshUrl: string | null, baseOrigin: string): URL {
  try {
    const current = new URL(currentSrc);
    const fresh = freshUrl ? new URL(freshUrl, baseOrigin) : null;

    return fresh && fresh.origin !== current.origin ? fresh : current;
  } catch (err) {
    console.warn('[Preview] pickReloadUrl: could not parse URL', currentSrc, freshUrl, err);

    return new URL(freshUrl ?? currentSrc, baseOrigin);
  }
}

/**
 * Origin of the preview iframe, used as the postMessage targetOrigin for the
 * element-selector bridge.
 *
 * `iframeUrl` is usually an absolute sandbox URL, but during Resume it can
 * transiently still hold a RELATIVE static-preview path (`/p/<chatId>/`) for
 * a render or two before the clearing effect commits. A bare `new URL(relative)`
 * throws "Invalid URL" and that throw escapes React's commit phase, blanking
 * the whole app. Resolving against the current origin makes a relative URL
 * safe (same-origin, which is exactly right for the same-origin static iframe)
 * and returns null only for genuinely unparseable input so callers can bail.
 */
export function previewOrigin(url: string): string | null {
  try {
    return new URL(url, defaultOrigin()).origin;
  } catch {
    return null;
  }
}

/**
 * URL of the captured static preview for a chat. Served by studio's
 * PreviewController from MinIO at /p/{chatId}/ — same origin as the app in
 * both dev and prod, and alive after the sandbox pod is gone. Never point
 * this at the {chatId}.preview.* ingress host: that host 502s once the
 * per-chat Ingress is deleted.
 *
 * `?embed=1` marks this as the owner's in-app preview iframe so the server
 * serves the in-app UX (no shared-preview banner, no "Resume in Dunea"
 * redirect page) rather than the external shared-viewer experience (DEV-111).
 * The shareable link that external viewers open is built separately and does
 * NOT carry this marker.
 */
export function getStaticPreviewUrl(chatId: string): string {
  return `/p/${chatId}/?embed=1`;
}

/**
 * Link the Share button copies: the sandbox's own host
 * (`https://{chatId}.preview.dunea.app/`) whenever the live preview runs on
 * one. That host stays safe to share after the sandbox sleeps — the wildcard
 * fallback sends it on to /p/{chatId}/, which serves the captured build.
 * Local dev (localhost port-forward) and static previews (relative /p/ path)
 * have no shareable host, so they fall back to the /p/ link on the app origin.
 */
export function getShareLink(baseUrl: string, chatId: string, appOrigin: string): string {
  const fallback = `${appOrigin}/p/${chatId}/`;

  try {
    const url = new URL(baseUrl);

    if (url.protocol !== 'https:' || url.hostname === 'localhost' || url.origin === appOrigin) {
      return fallback;
    }

    return `${url.origin}/`;
  } catch {
    return fallback;
  }
}

/** Versioned static capture of a checkpoint, served by studio's PreviewController. */
export function getVersionPreviewUrl(chatId: string, messageId: string): string {
  return `/p/${chatId}/v/${messageId}/?embed=1`;
}

/**
 * True when the preview host answers HTTP at all — any status, any redirect.
 *
 * The preview-url API only proves the pod EXISTS, not that its dev server is
 * serving. An iframe pointed at a host that refuses or resets the connection
 * renders the browser's own error page (Chrome's grey "sad page" icon), and
 * its onLoad still fires, so nothing downstream can tell it apart from a real
 * page. A no-cors fetch rejects on exactly those network-level failures and
 * resolves (opaque) for anything the server actually answered.
 */
export async function isPreviewReachable(url: string, timeoutMs = 5000, baseOrigin?: string): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    await fetch(new URL(url, baseOrigin ?? defaultOrigin()).toString(), {
      mode: 'no-cors',
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });

    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

export interface FetchPreviewUrlRetryOptions {
  /** Total attempts, including the first. */
  attempts: number;

  /** Delay before the first retry; doubles after each failure. */
  initialDelayMs: number;

  /** Ceiling for the backoff delay. Defaults to 10s. */
  maxDelayMs?: number;

  /** Checked before each attempt and after each wait (e.g. effect cleanup). */
  isCancelled?: () => boolean;
}

/**
 * Fetch a preview URL with bounded retries and exponential backoff.
 *
 * preview-url can fail transiently while the server revives a dead dev server
 * (and 503s during its failure cooldown). A single-shot fetch turns any of
 * those into a permanently dead board; unbounded fixed-interval polling turns
 * a deterministically broken pod into a request storm. Both call sites share
 * this middle ground instead.
 *
 * Resolves with the URL, or null once the attempt budget is exhausted or the
 * caller cancels. Never throws.
 */
export async function fetchPreviewUrlWithRetry(
  fetchUrl: () => Promise<string | null | undefined>,
  opts: FetchPreviewUrlRetryOptions,
): Promise<string | null> {
  const maxDelayMs = opts.maxDelayMs ?? 10_000;
  let delayMs = opts.initialDelayMs;

  for (let attempt = 0; attempt < opts.attempts; attempt++) {
    if (opts.isCancelled?.()) {
      return null;
    }

    try {
      const url = await fetchUrl();

      if (url) {
        return url;
      }
    } catch {
      // Not ready yet — fall through to the backoff wait.
    }

    if (attempt < opts.attempts - 1) {
      await new Promise((r) => setTimeout(r, delayMs));
      delayMs = Math.min(delayMs * 2, maxDelayMs);
    }
  }

  return null;
}
